import React, { useEffect, useMemo } from "react";
import { SideDrawer, XIcon } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";

import Styles from "./more-info-drawer.module.css";

type MoreInfoDrawerProps = {
  isDraweOpenOpen: boolean;
  setIsDraweOpenOpen: (e: boolean) => void;
  closeCta?: string;
  title?: string;
  content?: string;
};

function MoreInfoDrawer({
  isDraweOpenOpen,
  setIsDraweOpenOpen,
  closeCta,
  title,
  content = "",
}: Readonly<MoreInfoDrawerProps>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const windowSize = useWindowSize();
  const { t } = useWizardTranslation();

  useEffect(() => {
    setIsOpen(isDraweOpenOpen);
  }, [isDraweOpenOpen]);

  const drawerWidth = useMemo(() => {
    if (windowSize && windowSize < 600) {
      return 336;
    }
    if (windowSize && windowSize < 904) {
      return 376;
    }
    return 504;
  }, [windowSize]);

  return (
    <div data-testid="more-info-drawer-wrapper-test-id">
      <SideDrawer
        data-testid="more-info-drawer-test-id"
        wrapperStyles="rounded-l-2xl h-screen "
        drawerContentWrapperClassName="md:py-spacing-xxl sm:py-spacing-xl bg-surface-neutral-option-1  md:px-spacing-xxl sm:px-spacing-xl"
        drawerContentClassName="w-full"
        isOpen={isOpen}
        onCloseDrawer={() => {
          setIsOpen(false);
          setIsDraweOpenOpen(false);
        }}
        width={drawerWidth}
        closeBtnWrapperStyles="grid justify-items-end md:mb-spacing-l sm:mb-spacing-m hidden"
        closeButton={<div />}
      >
        <div className="flex justify-end">
          <LabelButtonWrapper
            data-testid="close-more-info-drawer-button"
            id="close-more-info-drawer-button"
            type="button"
            hierarchy="tertiary"
            label={closeCta ?? t("close", "Close")}
            onClick={() => {
              setIsOpen(false);
              setIsDraweOpenOpen(false);
            }}
            isRightIcon
            rightIcon={
              <XIcon
                stroke={selectedColorBrand["interactive-secondary-default"]}
              />
            }
            className={`text-[18px] font-semibold-font
          !px-spacing-xxs leading-[18px] h-[24px] text-border-accent-1-primary font-[600] hover:text-surface-neutral-option-1 ${Styles.closeBtn}`}
          />
        </div>
        <div className="mt-spacing-m md:mt-spacing-l">
          <h1 className="text-text-title font-header-font font-[400] text-[20px] md:text-[25px] leading-[105%] md:leading-[110%]">
            {title}
          </h1>

          <RichText
            className={`${Styles.drawerContent} mt-spacing-xs md:mt-spacing-m text-text-functional font-body-font font-[400] text-[18px] leading-[150%]`}
            richText={content}
          />
        </div>
      </SideDrawer>
    </div>
  );
}

export default MoreInfoDrawer;
